import { type MDXComponents } from "mdx/types";
import { AudioEmbed } from "./audio-embed.tsx";
import { Callout } from "./callout.tsx";
import { WordCardEmbed } from "./word-card-embed.tsx";

function slugify(children: unknown): string {
	if (typeof children === "string") {
		return children
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/(^-|-$)/g, "");
	}
	return "";
}

export const mdxComponents: MDXComponents = {
	h2: (props) => {
		const id = slugify(props.children);
		return (
			<h2
				id={id}
				className="mt-10 mb-3 scroll-mt-24 text-h3 sm:text-h2 text-foreground"
				style={{ fontFamily: "var(--serif-head)" }}
			>
				<a href={`#${id}`} className="no-underline hover:underline">
					{props.children}
				</a>
			</h2>
		);
	},

	h3: (props) => {
		const id = slugify(props.children);
		return (
			<h3
				id={id}
				className="mt-8 mb-2 scroll-mt-24 text-h4 sm:text-h3 text-foreground"
				style={{ fontFamily: "var(--serif-head)" }}
				{...props}
			/>
		);
	},

	h4: (props) => (
		<h4
			className="mt-6 mb-2 text-h5 text-foreground font-semibold"
			style={{ fontFamily: "var(--serif-head)" }}
			{...props}
		/>
	),

	p: (props) => (
		<p
			className="mb-6 text-body-md leading-relaxed text-foreground"
			style={{ fontFamily: "var(--serif)" }}
			{...props}
		/>
	),

	a: (props) => (
		<a
			className="text-blue-600 underline underline-offset-2 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
			target={
				props.href?.startsWith("http") ? "_blank" : undefined
			}
			rel={
				props.href?.startsWith("http")
					? "noopener noreferrer"
					: undefined
			}
			{...props}
		/>
	),

	ul: (props) => (
		<ul
			className="mb-6 list-disc space-y-2 pl-6 text-body-md text-foreground"
			style={{ fontFamily: "var(--serif)" }}
			{...props}
		/>
	),

	ol: (props) => (
		<ol
			className="mb-6 list-decimal space-y-2 pl-6 text-body-md text-foreground"
			style={{ fontFamily: "var(--serif)" }}
			{...props}
		/>
	),

	li: (props) => <li className="leading-relaxed" {...props} />,

	blockquote: (props) => (
		<blockquote
			className="my-6 rounded-r-lg border-l-4 border-green-600 bg-muted/30 p-4 italic text-card-foreground dark:border-green-500"
			style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
			{...props}
		/>
	),

	code: (props) => (
		<code
			className="rounded bg-muted px-1.5 py-0.5 text-body-sm text-foreground"
			style={{ fontFamily: "var(--mono)" }}
			{...props}
		/>
	),

	pre: (props) => (
		<pre
			className="mb-6 overflow-x-auto rounded-lg bg-muted p-4 text-body-sm"
			style={{ fontFamily: "var(--mono)" }}
			{...props}
		/>
	),

	strong: (props) => <strong className="font-semibold" {...props} />,
	em: (props) => <em className="italic" {...props} />,
	hr: () => <hr className="my-8 border-border" />,

	img: (props) => (
		<figure className="my-8">
			<img
				className="w-full rounded-lg border border-border"
				loading="lazy"
				{...props}
			/>
			{props.alt ? (
				<figcaption className="mt-2 text-center text-body-xs text-muted-foreground">
					{props.alt}
				</figcaption>
			) : null}
		</figure>
	),

	table: (props) => (
		<div className="mb-6 overflow-x-auto">
			<table
				className="w-full border-collapse border border-border text-body-sm"
				{...props}
			/>
		</div>
	),

	th: (props) => (
		<th
			className="border border-border bg-muted px-4 py-2 text-left font-semibold"
			{...props}
		/>
	),

	td: (props) => (
		<td className="border border-border px-4 py-2" {...props} />
	),

	// Custom MDX components
	AudioEmbed,
	WordCard: WordCardEmbed,
	Callout,
};
